import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Lock,
  Check,
  Plus,
  Edit2,
  Mail,
  Truck,
  ShieldCheck,
  CheckCircle2,
  Tag,
  Headphones,
  ShoppingBag,
  ArrowLeft,
  X,
  AlertCircle
} from 'lucide-react';
import {
  fetchUserAddressesFromBackend,
  addUserAddressBackend,
  createOrderBackend,
  createStripeCheckoutSessionBackend,
  clearCartBackend,
  getProductImageUrl,
  BACKEND_URL
} from '../services/api';

const Checkout = ({ cartItems = [], onUpdateQuantity, onRemoveItem, onClearCart }) => {
  const navigate = useNavigate();

  // User State Check
  const token = localStorage.getItem('userToken') || localStorage.getItem('token');
  const storedUserJson = localStorage.getItem('user');
  let loggedUser = null;
  try {
    if (storedUserJson) loggedUser = JSON.parse(storedUserJson);
  } catch (e) {}
  const isLoggedIn = !!token;

  // Account Details State
  const [email, setEmail] = useState(loggedUser?.email || '');
  const [phoneCountry, setPhoneCountry] = useState('+44');
  const [mobileNumber, setMobileNumber] = useState(loggedUser?.phone || '');

  // Saved Addresses State
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);

  const [savedAddresses, setSavedAddresses] = useState([]);

  // Load Customer Real Saved Addresses if Logged In
  useEffect(() => {
    let isMounted = true;
    const loadAddresses = async () => {
      if (isLoggedIn) {
        setLoadingAddresses(true);
        const addresses = await fetchUserAddressesFromBackend();
        if (isMounted && addresses && Array.isArray(addresses)) {
          setSavedAddresses(addresses);
          if (addresses.length > 0) {
            const defaultAddr = addresses.find(a => a.is_default) || addresses[0];
            if (defaultAddr) setSelectedAddressId(defaultAddr.id);
          }
        }
        if (isMounted) setLoadingAddresses(false);
      } else {
        if (isMounted) setLoadingAddresses(false);
      }
    };
    loadAddresses();
    return () => { isMounted = false; };
  }, [isLoggedIn]);

  // Guest Address Form State (for logged out or users with no saved address)
  const [guestAddress, setGuestAddress] = useState({
    full_name: loggedUser?.name || '',
    street_address: '',
    city: '',
    postal_code: '',
    country: 'United Kingdom'
  });

  // Billing Address State
  const [billingSameAsDelivery, setBillingSameAsDelivery] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    full_name: '',
    street_address: '',
    city: '',
    postal_code: '',
    country: 'United Kingdom'
  });

  // Modal New Address Form State
  const [newAddrForm, setNewAddrForm] = useState({
    label: 'Home',
    full_name: loggedUser?.name || '',
    phone: `${phoneCountry} ${mobileNumber}`,
    street_address: '',
    city: '',
    postal_code: '',
    country: 'United Kingdom',
    is_default: false
  });
  const [savingAddr, setSavingAddr] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [orderError, setOrderError] = useState('');

  // Shipping Method Selection
  const [selectedShippingMethod, setSelectedShippingMethod] = useState('standard');

  // Coupon State (Reads pre-applied coupon from sessionStorage or user input)
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const saved = sessionStorage.getItem('appliedCoupon');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.code) return parsed;
      }
    } catch (e) {}
    return null;
  });

  const [couponCodeInput, setCouponCodeInput] = useState('');
  const [couponMsg, setCouponMsg] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [shippingConfig, setShippingConfig] = useState({
    standard_shipping_fee: 15.00,
    free_shipping_threshold: 500.00,
    vat_tax_percent: 20.00
  });

  // Load Shipping & Tax Settings
  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings && isMounted) {
            setShippingConfig({
              standard_shipping_fee: Number(data.settings.standard_shipping_fee || data.settings.shipping_rate || 15.00),
              free_shipping_threshold: Number(data.settings.free_shipping_threshold || 500.00),
              vat_tax_percent: Number(data.settings.vat_tax_percent !== undefined && data.settings.vat_tax_percent !== null ? data.settings.vat_tax_percent : 20.00)
            });
          }
        }
      } catch (e) {}
    };
    fetchSettings();
    return () => { isMounted = false; };
  }, []);

  // Calculate Subtotal & Totals
  const validItems = cartItems.filter(item => !item.is_out_of_stock && (item.stock === undefined || item.stock > 0));
  const rawSubtotal = validItems.reduce((acc, item) => acc + Number(item.price || item.sale_price || 0) * (item.quantity || 1), 0);
  
  const discountAmount = appliedCoupon
    ? (appliedCoupon.discountAmount !== undefined
        ? Number(appliedCoupon.discountAmount)
        : (rawSubtotal * Number(appliedCoupon.discountPct || appliedCoupon.discount_percent || 0)) / 100)
    : 0;
  const subtotalAfterDiscount = Math.max(0, rawSubtotal - discountAmount);

  // Dynamic Shipping Fee
  const isFreeShipping = subtotalAfterDiscount >= shippingConfig.free_shipping_threshold;
  let baseShippingCost = isFreeShipping ? 0 : shippingConfig.standard_shipping_fee;
  if (selectedShippingMethod === 'express') {
    baseShippingCost += 9.99;
  }

  const taxAmount = subtotalAfterDiscount * (shippingConfig.vat_tax_percent / 100);
  const grandTotal = subtotalAfterDiscount + baseShippingCost + taxAmount;

  // Add Address Handler
  const handleCreateAddressSubmit = async (e) => {
    e.preventDefault();
    if (!newAddrForm.full_name || !newAddrForm.street_address) return;

    setSavingAddr(true);
    if (isLoggedIn) {
      const res = await addUserAddressBackend(newAddrForm);
      if (res && res.address) {
        setSavedAddresses([res.address, ...savedAddresses]);
        setSelectedAddressId(res.address.id);
      }
    } else {
      const created = {
        id: 'addr_' + Date.now(),
        ...newAddrForm
      };
      setSavedAddresses([created, ...savedAddresses]);
      setSelectedAddressId(created.id);
    }
    setSavingAddr(false);
    setShowAddAddressModal(false);
  };

  // Coupon Validation Handler
  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponCodeInput.trim()) return;
    setApplyingCoupon(true);
    setCouponMsg('');

    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          code: couponCodeInput,
          cartTotal: rawSubtotal,
          email: email || null
        })
      });
      const data = await response.json();
      if (data.success && data.coupon) {
        const couponObj = {
          code: data.coupon.code,
          discountPct: Number(data.coupon.discount_percent || 0),
          discountAmount: Number(data.coupon.discount_amount || 0),
          discountType: data.coupon.discount_type || 'percentage'
        };
        setAppliedCoupon(couponObj);
        sessionStorage.setItem('appliedCoupon', JSON.stringify(couponObj));
        setCouponMsg(`✔ Coupon ${data.coupon.code} applied successfully!`);
        setCouponCodeInput('');
      } else {
        setCouponMsg(data.message || 'Invalid coupon code');
      }
    } catch (err) {
      setCouponMsg('Error validating coupon');
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Continue to Payment / Order Placement
  const handleContinueToPayment = async () => {
    setOrderError('');

    if (!validItems || validItems.length === 0) {
      setOrderError('Your cart is empty or the selected items are out of stock. Please add items to your cart.');
      return;
    }

    const errors = {};

    // Validate Contact Info
    if (!email || !email.trim()) {
      errors.email = 'Please enter your email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = 'Please enter a valid email address.';
    }

    if (!mobileNumber || !mobileNumber.trim()) {
      errors.mobileNumber = 'Please enter your mobile phone number.';
    }

    let finalShippingAddress = null;

    if (savedAddresses.length > 0) {
      const found = savedAddresses.find(a => a.id === selectedAddressId) || savedAddresses[0];
      finalShippingAddress = found;
    } else {
      // User typed into Delivery Address Form
      if (!guestAddress.full_name || !guestAddress.full_name.trim()) {
        errors.full_name = 'Recipient full name is required.';
      }
      if (!guestAddress.street_address || !guestAddress.street_address.trim()) {
        errors.street_address = 'Street address is required.';
      }
      if (!guestAddress.city || !guestAddress.city.trim()) {
        errors.city = 'City is required.';
      }
      if (!guestAddress.postal_code || !guestAddress.postal_code.trim()) {
        errors.postal_code = 'Postcode / ZIP is required.';
      }

      finalShippingAddress = {
        ...guestAddress,
        phone: `${phoneCountry} ${mobileNumber}`
      };
    }

    // Determine and Validate Billing Address
    let finalBillingAddress = null;
    if (billingSameAsDelivery) {
      finalBillingAddress = finalShippingAddress;
    } else {
      if (!billingAddress.full_name || !billingAddress.full_name.trim()) {
        errors.billing_full_name = 'Billing recipient name is required.';
      }
      if (!billingAddress.street_address || !billingAddress.street_address.trim()) {
        errors.billing_street_address = 'Billing street address is required.';
      }
      if (!billingAddress.city || !billingAddress.city.trim()) {
        errors.billing_city = 'Billing city is required.';
      }
      if (!billingAddress.postal_code || !billingAddress.postal_code.trim()) {
        errors.billing_postal_code = 'Billing postcode / ZIP is required.';
      }

      finalBillingAddress = {
        ...billingAddress,
        phone: `${phoneCountry} ${mobileNumber}`
      };
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setOrderError('Please fill in the required fields marked in red below to proceed.');
      return;
    }

    setFormErrors({});
    setSubmittingOrder(true);
    try {
      const orderPayload = {
        guest_email: !isLoggedIn ? email : null,
        guest_phone: `${phoneCountry} ${mobileNumber}`,
        shipping_address: finalShippingAddress,
        billing_address: finalBillingAddress,
        shipping_method: selectedShippingMethod,
        subtotal: rawSubtotal,
        discount_amount: discountAmount,
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
        shipping_fee: baseShippingCost,
        tax_amount: taxAmount,
        total_amount: grandTotal,
        items: validItems
      };

      const res = await createOrderBackend(orderPayload);
      if (res && res.success) {
        // Clear guest cart in DB before logging in as newly created account
        await clearCartBackend();
        if (onClearCart) onClearCart();

        // Auto-login Guest User if token returned
        if (res.token) {
          localStorage.setItem('userToken', res.token);
          localStorage.setItem('token', res.token);
          if (res.user) localStorage.setItem('user', JSON.stringify(res.user));
          // Clear logged-in user cart in DB as well
          await clearCartBackend();
        }

        // Save pending order to sessionStorage
        const createdOrder = res.order;
        sessionStorage.setItem('pendingOrder', JSON.stringify(createdOrder));
        sessionStorage.removeItem('appliedCoupon');

        // Request Stripe Hosted Checkout session
        const stripeRes = await createStripeCheckoutSessionBackend(createdOrder.id);
        if (stripeRes && stripeRes.success && stripeRes.url) {
          window.location.href = stripeRes.url;
          return;
        } else {
          setOrderError(stripeRes?.message || "Failed to initialize Stripe checkout. Please try again.");
        }
      } else {
        setOrderError(res?.message || "Could not complete order. Please check item availability.");
      }
    } catch (err) {
      setOrderError("Order placement failed. Please verify your details and try again.");
    } finally {
      setSubmittingOrder(false);
    }
  };

  return (
    <div className="checkout-page-container" style={{ minHeight: '100vh', background: '#fafbfc', padding: '40px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        
        {/* 1. BREADCRUMBS & HEADER */}
        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>&gt;</span>
          <Link to="/cart" style={{ color: '#64748b', textDecoration: 'none' }}>Cart</Link>
          <span style={{ margin: '0 8px' }}>&gt;</span>
          <span style={{ color: '#0f172a', fontWeight: 600 }}>Checkout</span>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '36px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', margin: '0 0 6px 0' }}>
            Checkout
          </h1>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13.5px', color: '#64748b', fontWeight: 700 }}>
            <Lock size={14} color="#64748b" />
            <span>Secure Checkout</span>
          </div>
        </div>

        {/* MAIN SPLIT GRID */}
        <div className="cart-page-grid" style={{ gridTemplateColumns: '1fr 400px' }}>
          
          {/* LEFT COLUMN: MULTI-STEP CHECKOUT FORM */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', minWidth: 0 }}>
            
            {/* STEP 1: ACCOUNT DETAILS MATCHING chckout.png 1:1 */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', fontWeight: 900, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fee2e2' }}>
                    1
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Account Details</h3>
                </div>

                {!isLoggedIn && (
                  <span style={{ fontSize: '12.5px', color: '#64748b', fontWeight: 600 }}>
                    💡 <em>If you have an account, enter your registered email below.</em>
                  </span>
                )}
              </div>

              {/* Email Address Input */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.email ? '#dc2626' : '#475569', marginBottom: '6px' }}>Email Address *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (formErrors.email) setFormErrors({ ...formErrors, email: undefined });
                    }}
                    placeholder="name@example.com"
                    style={{
                      width: '100%',
                      padding: '12px 40px 12px 16px',
                      borderRadius: '10px',
                      border: formErrors.email ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                      background: formErrors.email ? '#fff5f5' : '#ffffff',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#0f172a',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  {!formErrors.email && email && <Check size={18} color="#16a34a" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }} />}
                </div>
                {formErrors.email ? (
                  <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.email}</span>
                ) : (
                  <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'block' }}>We'll use this email to send you order updates & receipts.</span>
                )}
              </div>

              {/* Mobile Number Input */}
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.mobileNumber ? '#dc2626' : '#475569', marginBottom: '6px' }}>Mobile Number *</label>
                <div style={{ display: 'flex', gap: '10px', position: 'relative' }}>
                  <select
                    value={phoneCountry}
                    onChange={(e) => setPhoneCountry(e.target.value)}
                    style={{ width: '95px', padding: '12px 10px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 700, color: '#0f172a', background: '#ffffff', outline: 'none' }}
                  >
                    <option value="+44">+44 (UK)</option>
                    <option value="+1">+1 (US)</option>
                    <option value="+33">+33 (FR)</option>
                    <option value="+49">+49 (DE)</option>
                  </select>

                  <div style={{ flex: 1, position: 'relative' }}>
                    <input
                      type="text"
                      value={mobileNumber}
                      onChange={(e) => {
                        setMobileNumber(e.target.value);
                        if (formErrors.mobileNumber) setFormErrors({ ...formErrors, mobileNumber: undefined });
                      }}
                      placeholder="7123 456789"
                      style={{
                        width: '100%',
                        padding: '12px 40px 12px 16px',
                        borderRadius: '10px',
                        border: formErrors.mobileNumber ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                        background: formErrors.mobileNumber ? '#fff5f5' : '#ffffff',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#0f172a',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {!formErrors.mobileNumber && mobileNumber && <Check size={18} color="#16a34a" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }} />}
                  </div>
                </div>
                {formErrors.mobileNumber && (
                  <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.mobileNumber}</span>
                )}
              </div>
            </div>

            {/* STEP 2: DELIVERY INFORMATION MATCHING chckout.png 1:1 */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', fontWeight: 900, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fee2e2' }}>
                    2
                  </div>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Delivery Information</h3>
                    <span style={{ fontSize: '12.5px', color: '#64748b' }}>Select or add a delivery address</span>
                  </div>
                </div>

                <button
                  onClick={() => setShowAddAddressModal(true)}
                  style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '13px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  <Plus size={15} />
                  <span>Add New Address</span>
                </button>
              </div>

              {/* CASE A: LOGGED IN WITH SAVED ADDRESS CARDS LIST */}
              {savedAddresses.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
                  {loadingAddresses ? (
                    <div style={{ padding: '20px', color: '#64748b', fontSize: '13px' }}>Loading saved addresses...</div>
                  ) : (
                    savedAddresses.map((addr) => {
                      const isSelected = selectedAddressId === addr.id;

                      return (
                        <div
                          key={addr.id}
                          onClick={() => setSelectedAddressId(addr.id)}
                          style={{
                            border: isSelected ? '2px solid #dc2626' : '1px solid #e2e8f0',
                            background: isSelected ? '#fff5f5' : '#ffffff',
                            borderRadius: '14px',
                            padding: '18px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: '16px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '14px' }}>
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                border: isSelected ? '6px solid #dc2626' : '2px solid #cbd5e1',
                                background: '#ffffff',
                                flexShrink: 0,
                                marginTop: '2px'
                              }}
                            />

                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                <span style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a' }}>{addr.label || 'Home'}</span>
                                {addr.is_default && (
                                  <span style={{ fontSize: '10.5px', fontWeight: 800, color: '#64748b', background: '#e2e8f0', padding: '2px 8px', borderRadius: '4px' }}>Default</span>
                                )}
                              </div>

                              <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>{addr.full_name || addr.name}</div>
                              <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.4 }}>{addr.street_address || addr.street}, {addr.city}, {addr.postal_code || addr.postcode}, {addr.country}</div>
                              <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '4px' }}>{addr.phone}</div>
                            </div>
                          </div>

                          <button
                            onClick={(e) => { e.stopPropagation(); setShowAddAddressModal(true); }}
                            style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            <Edit2 size={13} />
                            <span>Edit</span>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                /* CASE B: LOGGED OUT OR USER WITH NO SAVED ADDRESSES -> RENDER INPUT FORM */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.full_name ? '#dc2626' : '#475569', marginBottom: '6px' }}>Full Recipient Name *</label>
                    <input
                      type="text"
                      value={guestAddress.full_name}
                      onChange={(e) => {
                        setGuestAddress({ ...guestAddress, full_name: e.target.value });
                        if (formErrors.full_name) setFormErrors({ ...formErrors, full_name: undefined });
                      }}
                      placeholder="e.g. John Doe"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: formErrors.full_name ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                        background: formErrors.full_name ? '#fff5f5' : '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {formErrors.full_name && (
                      <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.full_name}</span>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.street_address ? '#dc2626' : '#475569', marginBottom: '6px' }}>Street Address *</label>
                    <input
                      type="text"
                      value={guestAddress.street_address}
                      onChange={(e) => {
                        setGuestAddress({ ...guestAddress, street_address: e.target.value });
                        if (formErrors.street_address) setFormErrors({ ...formErrors, street_address: undefined });
                      }}
                      placeholder="Flat 12, Windsor House, 21 Baker Street"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: formErrors.street_address ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                        background: formErrors.street_address ? '#fff5f5' : '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {formErrors.street_address && (
                      <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.street_address}</span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.city ? '#dc2626' : '#475569', marginBottom: '6px' }}>City *</label>
                      <input
                        type="text"
                        value={guestAddress.city}
                        onChange={(e) => {
                          setGuestAddress({ ...guestAddress, city: e.target.value });
                          if (formErrors.city) setFormErrors({ ...formErrors, city: undefined });
                        }}
                        placeholder="London"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          border: formErrors.city ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                          background: formErrors.city ? '#fff5f5' : '#ffffff',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {formErrors.city && (
                        <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.city}</span>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.postal_code ? '#dc2626' : '#475569', marginBottom: '6px' }}>Postcode / ZIP *</label>
                      <input
                        type="text"
                        value={guestAddress.postal_code}
                        onChange={(e) => {
                          setGuestAddress({ ...guestAddress, postal_code: e.target.value });
                          if (formErrors.postal_code) setFormErrors({ ...formErrors, postal_code: undefined });
                        }}
                        placeholder="W1U 3BW"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          border: formErrors.postal_code ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                          background: formErrors.postal_code ? '#fff5f5' : '#ffffff',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {formErrors.postal_code && (
                        <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.postal_code}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Order Updates Channel Box */}
              <div className="checkout-updates-box" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                  <Mail size={18} color="#64748b" style={{ flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a' }}>Where should we send your order updates?</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>You can change this anytime.</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', overflow: 'hidden' }}>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', wordBreak: 'break-all' }}>{email || 'No email entered'}</span>
                  <button onClick={() => { const el = document.querySelector('input[type="email"]'); if (el) el.focus(); }} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '12.5px', fontWeight: 800, cursor: 'pointer' }}>Edit</button>
                </div>
              </div>
            </div>

            {/* STEP 3: BILLING ADDRESS */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', fontWeight: 900, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fee2e2' }}>
                  3
                </div>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Billing Address</h3>
                  <span style={{ fontSize: '12.5px', color: '#64748b' }}>Select the address that matches your payment method</span>
                </div>
              </div>

              {/* Two Option Cards: Same as Delivery vs Different */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: billingSameAsDelivery ? '0' : '20px' }}>
                <label
                  onClick={() => setBillingSameAsDelivery(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: billingSameAsDelivery ? '2px solid #dc2626' : '1px solid #e2e8f0',
                    background: billingSameAsDelivery ? '#fff5f5' : '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="radio"
                    name="billing_option"
                    checked={billingSameAsDelivery}
                    onChange={() => setBillingSameAsDelivery(true)}
                    style={{ accentColor: '#dc2626', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>Same as delivery address</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Use the delivery address entered above for billing and tax invoices.</div>
                  </div>
                </label>

                <label
                  onClick={() => setBillingSameAsDelivery(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: !billingSameAsDelivery ? '2px solid #dc2626' : '1px solid #e2e8f0',
                    background: !billingSameAsDelivery ? '#fff5f5' : '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="radio"
                    name="billing_option"
                    checked={!billingSameAsDelivery}
                    onChange={() => setBillingSameAsDelivery(false)}
                    style={{ accentColor: '#dc2626', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>Use a different billing address</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Specify a separate billing address for payment authorization.</div>
                  </div>
                </label>
              </div>

              {/* Form fields if different billing address is selected */}
              {!billingSameAsDelivery && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.billing_full_name ? '#dc2626' : '#475569', marginBottom: '6px' }}>Billing Full Name *</label>
                    <input
                      type="text"
                      value={billingAddress.full_name}
                      onChange={(e) => {
                        setBillingAddress({ ...billingAddress, full_name: e.target.value });
                        if (formErrors.billing_full_name) setFormErrors({ ...formErrors, billing_full_name: undefined });
                      }}
                      placeholder="e.g. John Doe"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: formErrors.billing_full_name ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                        background: formErrors.billing_full_name ? '#fff5f5' : '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {formErrors.billing_full_name && (
                      <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.billing_full_name}</span>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.billing_street_address ? '#dc2626' : '#475569', marginBottom: '6px' }}>Billing Street Address *</label>
                    <input
                      type="text"
                      value={billingAddress.street_address}
                      onChange={(e) => {
                        setBillingAddress({ ...billingAddress, street_address: e.target.value });
                        if (formErrors.billing_street_address) setFormErrors({ ...formErrors, billing_street_address: undefined });
                      }}
                      placeholder="Billing Address (e.g. Flat 4, 15 High Street)"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: formErrors.billing_street_address ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                        background: formErrors.billing_street_address ? '#fff5f5' : '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {formErrors.billing_street_address && (
                      <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.billing_street_address}</span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.billing_city ? '#dc2626' : '#475569', marginBottom: '6px' }}>City *</label>
                      <input
                        type="text"
                        value={billingAddress.city}
                        onChange={(e) => {
                          setBillingAddress({ ...billingAddress, city: e.target.value });
                          if (formErrors.billing_city) setFormErrors({ ...formErrors, billing_city: undefined });
                        }}
                        placeholder="London"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          border: formErrors.billing_city ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                          background: formErrors.billing_city ? '#fff5f5' : '#ffffff',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {formErrors.billing_city && (
                        <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.billing_city}</span>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: formErrors.billing_postal_code ? '#dc2626' : '#475569', marginBottom: '6px' }}>Postcode / ZIP *</label>
                      <input
                        type="text"
                        value={billingAddress.postal_code}
                        onChange={(e) => {
                          setBillingAddress({ ...billingAddress, postal_code: e.target.value });
                          if (formErrors.billing_postal_code) setFormErrors({ ...formErrors, billing_postal_code: undefined });
                        }}
                        placeholder="SW1A 1AA"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          border: formErrors.billing_postal_code ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                          background: formErrors.billing_postal_code ? '#fff5f5' : '#ffffff',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {formErrors.billing_postal_code && (
                        <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.billing_postal_code}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* STEP 4: SHIPPING MATCHING SIMPLE FEE / FREE DISPLAY */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#fef2f2', color: '#dc2626', fontWeight: 900, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fee2e2' }}>
                    4
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Shipping</h3>
                </div>

                <span style={{ fontSize: '15px', fontWeight: 900, color: isFreeShipping ? '#16a34a' : '#0f172a', background: isFreeShipping ? '#dcfce7' : '#f8fafc', padding: '4px 14px', borderRadius: '9999px', border: isFreeShipping ? '1px solid #bbf7d0' : '1px solid #e2e8f0' }}>
                  {isFreeShipping ? 'FREE' : `£${shippingConfig.standard_shipping_fee.toFixed(2)}`}
                </span>
              </div>

              <div style={{ fontSize: '13.5px', color: '#64748b', marginBottom: '24px', lineHeight: 1.5 }}>
                {isFreeShipping ? 'Your order qualifies for complimentary Free Shipping.' : `Standard shipping fee of £${shippingConfig.standard_shipping_fee.toFixed(2)} will be applied.`}
              </div>

              {/* Inline Form / Order Error Message */}
              {orderError && (
                <div style={{
                  padding: '14px 18px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '12px',
                  color: '#b91c1c',
                  fontSize: '13.5px',
                  fontWeight: 700,
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                  <span>{orderError}</span>
                </div>
              )}

              {/* Continue to Payment Button */}
              <button
                onClick={handleContinueToPayment}
                disabled={submittingOrder}
                style={{
                  background: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 36px',
                  fontSize: '15px',
                  fontWeight: 800,
                  cursor: submittingOrder ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 6px 18px rgba(220, 38, 38, 0.35)'
                }}
              >
                <span>{submittingOrder ? 'Redirecting to Stripe...' : 'Continue to Payment'}</span>
                <span>➔</span>
              </button>

            </div>

          </div>

          {/* RIGHT COLUMN: ORDER SUMMARY CARD */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>
            
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', marginBottom: '20px' }}>
                Order Summary
              </h3>

              {/* PRODUCT ITEM MINI LIST */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '20px', marginBottom: '20px' }}>
                {validItems.map((item) => (
                  <div key={item.cart_item_id || item.id} style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                    <div style={{ width: '60px', height: '60px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', flexShrink: 0 }}>
                      <img src={getProductImageUrl(item.image_url || item.image || '/uploads/panel_72led.jpg')} alt={item.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                    </div>

                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>{item.name}</h4>
                      {Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '3px 0' }}>
                          {item.selected_addons.map((addon, aIdx) => (
                            <span key={aIdx} style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '1px 6px', borderRadius: '4px' }}>
                              + {addon.addon_name || addon.name} (+£{Number(addon.addon_price || addon.price || 0).toFixed(2)})
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px' }}>Qty: {item.quantity || 1}</div>
                    </div>

                    <div style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a' }}>
                      £{(Number(item.price || 0) * (item.quantity || 1)).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Subtotal Breakdown */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13.5px', color: '#64748b' }}>
                <span>Subtotal ({validItems.length} items)</span>
                <span style={{ fontWeight: 800, color: '#0f172a' }}>£{rawSubtotal.toFixed(2)}</span>
              </div>

              {/* Shipping Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13.5px', color: '#64748b' }}>
                <span>Shipping</span>
                <span style={{ fontWeight: 800, color: baseShippingCost === 0 ? '#16a34a' : '#0f172a' }}>
                  {baseShippingCost === 0 ? 'Free' : `£${baseShippingCost.toFixed(2)}`}
                </span>
              </div>

              {/* Applied Discount Row */}
              {discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13.5px', color: '#16a34a', fontWeight: 700 }}>
                  <span>Discount ({appliedCoupon.code})</span>
                  <span>-£{discountAmount.toFixed(2)}</span>
                </div>
              )}

              {/* VAT Tax Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '13.5px', color: '#64748b' }}>
                <span>Tax ({shippingConfig.vat_tax_percent}%)</span>
                <span style={{ fontWeight: 800, color: '#0f172a' }}>£{taxAmount.toFixed(2)}</span>
              </div>

              {/* Total Line Divider */}
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '18px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>Total</span>
                  <div style={{ fontSize: '11.5px', color: '#64748b' }}>Including £{taxAmount.toFixed(2)} Tax</div>
                </div>
                <span style={{ fontSize: '28px', fontWeight: 900, color: '#dc2626' }}>
                  £{grandTotal.toFixed(2)}
                </span>
              </div>

              {/* Promo Code Input Box */}
              {!appliedCoupon ? (
                <form onSubmit={handleApplyCoupon} style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    value={couponCodeInput}
                    onChange={(e) => setCouponCodeInput(e.target.value.toUpperCase())}
                    placeholder="Promo Code (e.g. SAVE10)"
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 700, outline: 'none' }}
                  />
                  <button
                    type="submit"
                    disabled={applyingCoupon || !couponCodeInput.trim()}
                    style={{ background: '#0f172a', color: '#ffffff', border: 'none', borderRadius: '10px', padding: '10px 16px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}
                  >
                    {applyingCoupon ? '...' : 'Apply'}
                  </button>
                </form>
              ) : (
                /* Applied Coupon Card */
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Tag size={16} color="#16a34a" />
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#166534' }}>Coupon Applied!</div>
                      <div style={{ fontSize: '13px', fontWeight: 900, color: '#15803d' }}>{appliedCoupon.code} ({appliedCoupon.discountPct}% OFF)</div>
                    </div>
                  </div>
                  <button onClick={() => { setAppliedCoupon(null); sessionStorage.removeItem('appliedCoupon'); setCouponMsg(''); }} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
                    Remove
                  </button>
                </div>
              )}
              {couponMsg && (
                <div style={{ fontSize: '12px', fontWeight: 700, color: couponMsg.startsWith('✔') ? '#16a34a' : '#dc2626', marginBottom: '16px' }}>
                  {couponMsg}
                </div>
              )}

              {/* Guarantees List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12.5px', color: '#475569', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle2 size={15} color="#dc2626" />
                  <span>60-day money back guarantee</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShieldCheck size={15} color="#dc2626" />
                  <span>2-year warranty on all panels</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Lock size={15} color="#dc2626" />
                  <span>Safe & secure checkout</span>
                </div>
              </div>

              {/* Payment Logos */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', opacity: 0.75 }}>
                <span style={{ fontSize: '11px', fontWeight: 900, color: '#1e3a8a', fontStyle: 'italic' }}>VISA</span>
                <span style={{ fontSize: '11px', fontWeight: 900, color: '#ea580c' }}>Mastercard</span>
                <span style={{ fontSize: '11px', fontWeight: 900, color: '#0284c7' }}>PayPal</span>
                <span style={{ fontSize: '11px', fontWeight: 900, color: '#0f172a' }}>Pay</span>
                <span style={{ fontSize: '11px', fontWeight: 900, color: '#475569' }}>G Pay</span>
              </div>

            </div>

            {/* NEED HELP SUPPORT CARD */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Headphones size={24} color="#64748b" style={{ flexShrink: 0 }} />
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>Need help?</h4>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px 0' }}>Our support team is here for you.</p>
                <Link to="/contact" style={{ fontSize: '12.5px', fontWeight: 800, color: '#dc2626', textDecoration: 'none' }}>
                  Contact Support ➔
                </Link>
              </div>
            </div>

          </div>

        </div>

      </div>

      {/* ADD NEW ADDRESS MODAL */}
      {showAddAddressModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '20px', padding: '28px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Add New Delivery Address</h3>
              <button onClick={() => setShowAddAddressModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={18} color="#64748b" />
              </button>
            </div>

            <form onSubmit={handleCreateAddressSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '4px' }}>Address Label</label>
                <select
                  value={newAddrForm.label}
                  onChange={(e) => setNewAddrForm({ ...newAddrForm, label: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none' }}
                >
                  <option value="Home">Home</option>
                  <option value="Work">Work</option>
                  <option value="Office">Office</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '4px' }}>Full Recipient Name *</label>
                <input
                  type="text"
                  value={newAddrForm.full_name}
                  onChange={(e) => setNewAddrForm({ ...newAddrForm, full_name: e.target.value })}
                  placeholder="e.g. Kashish Gupta"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '4px' }}>Phone Number *</label>
                <input
                  type="text"
                  value={newAddrForm.phone}
                  onChange={(e) => setNewAddrForm({ ...newAddrForm, phone: e.target.value })}
                  placeholder="+44 7123 456789"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '4px' }}>Street Address *</label>
                <input
                  type="text"
                  value={newAddrForm.street_address}
                  onChange={(e) => setNewAddrForm({ ...newAddrForm, street_address: e.target.value })}
                  placeholder="Flat 12, Windsor House, 21 Baker Street"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '4px' }}>City *</label>
                  <input
                    type="text"
                    value={newAddrForm.city}
                    onChange={(e) => setNewAddrForm({ ...newAddrForm, city: e.target.value })}
                    placeholder="London"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#475569', marginBottom: '4px' }}>Postcode / ZIP *</label>
                  <input
                    type="text"
                    value={newAddrForm.postal_code}
                    onChange={(e) => setNewAddrForm({ ...newAddrForm, postal_code: e.target.value })}
                    placeholder="W1U 3BW"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddAddressModal(false)} style={{ background: '#f1f5f9', border: 'none', padding: '10px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={savingAddr} style={{ background: '#dc2626', border: 'none', padding: '10px 22px', borderRadius: '8px', fontSize: '13px', fontWeight: 800, color: '#ffffff', cursor: 'pointer' }}>
                  {savingAddr ? 'Saving...' : 'Save Address'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Checkout;
